import { useCallback, useEffect, useRef, useState } from 'react';

import { track } from '@/services/analytics';

import {
  checkPhoto,
  isHeic,
  readSizeInBrowser,
  type PhotoSize,
} from './photo-preflight';

/**
 * Выбранные фотографии и их превью.
 *
 * Файлы никуда не загружаются до нажатия «Создать портрет»: они уходят одним
 * multipart-запросом вместе с выбором. До этого момента снимок живёт только
 * в памяти вкладки.
 */
export interface SelectedPhoto {
  file: File;
  /** null для HEIC — браузер такой файл не отрисует. */
  previewUrl: string | null;
}

interface Options {
  max: number;
  readSize?: (file: File) => Promise<PhotoSize | null>;
}

export function usePhotoSelection({ max, readSize = readSizeInBrowser }: Options) {
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [issues, setIssues] = useState<string[]>([]);

  // Освобождать превью нужно и при размонтировании, но эффект очистки не
  // должен зависеть от списка: иначе он срабатывал бы на каждое добавление
  // и отзывал ссылки у ещё показанных снимков. Отсюда ref с последним
  // списком, который читает только функция очистки.
  const photosRef = useRef<SelectedPhoto[]>([]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(
    () => () => {
      for (const photo of photosRef.current) {
        if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
      }
    },
    [],
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      const accepted: SelectedPhoto[] = [];
      const rejected: string[] = [];
      let free = max - photos.length;

      for (const file of files) {
        if (free <= 0) {
          rejected.push(`Больше ${max} фотографий добавить нельзя.`);
          break;
        }

        const issue = await checkPhoto(file, { readSize });

        if (issue) {
          rejected.push(`${file.name}: ${issue.message}`);
          continue;
        }

        accepted.push({
          file,
          previewUrl: isHeic(file) ? null : URL.createObjectURL(file),
        });
        free -= 1;
      }

      setPhotos((current) => [...current, ...accepted]);
      setIssues(rejected);

      // Считаются принятые снимки, а не выбранные в диалоге: отклонённые
      // проверками файлы до сервера не доходят и воронку не наполняют.
      if (accepted.length > 0) {
        track('photo_uploaded', { count: accepted.length, rejected: rejected.length });
      }
    },
    [max, photos.length, readSize],
  );

  const remove = useCallback((index: number) => {
    setPhotos((current) => {
      const photo = current[index];
      if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);

      return current.filter((_, position) => position !== index);
    });
  }, []);

  return { photos, issues, addFiles, remove };
}
